import * as amplitude from "@amplitude/analytics-browser";
import { Flex, Link, Skeleton, Text } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { GenericError } from "src/components/Feedback/GenericError";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { Table } from "src/components/Table/Table";
import { useGetCapacitiesHistoryKey } from "src/generated/hooks/form-service/queries";
import {
  getCapacitiesHistory,
  GetCapacitiesHistoryResponse,
} from "src/generated/hooks/form-service/requests";
import { useGlossary } from "src/hooks/useGlossary";
import { useOrganization } from "src/hooks/useOrganization";
import { formatIsoDateToOrg, maybePluralize } from "src/services/format";
import * as RD from "src/types/remoteData";

export type SelectedTransaction = {
  transactionId: number;
  timestamp: string;
  userRole: string;
  userFullName: string;
};

export type Props = {
  enrollmentPeriodId: string;
  onSelectTransaction: (item: SelectedTransaction) => void;
};

type HistoryRow = {
  transactionId: number;
  schoolCount: number;
  timestamp: string | null;
  userFullName: string;
  userRole: string;
};

const TIMESTAMP_FORMAT = "MM/dd/yyyy, h:mm a zzz";

export const History: React.FC<Props> = ({
  enrollmentPeriodId,
  onSelectTransaction,
}) => {
  const organization = useOrganization();
  const { glossary } = useGlossary();

  const query = useQuery({
    queryKey: [useGetCapacitiesHistoryKey, { path: { enrollmentPeriodId } }],
    queryFn: () =>
      getCapacitiesHistory({
        path: { enrollmentPeriodId },
      }).then((response) => response.data),
  });

  const historyRD: RD.RemoteData<Error | null, GetCapacitiesHistoryResponse> =
    useMemo(() => RD.fromRestResult(query), [query]);

  const columns: ColumnDef<HistoryRow>[] = useMemo(
    () => [
      {
        id: "entry",
        cell: ({ row }) => {
          const { schoolCount, userRole, userFullName, timestamp } =
            row.original;
          const formattedTimestamp = timestamp
            ? formatIsoDateToOrg(timestamp, organization, TIMESTAMP_FORMAT)
            : "";
          const schoolWord = glossary`${maybePluralize(schoolCount, "school")}`;

          return (
            <Text>
              <Text as="span" fontWeight="bold">
                {"Capacities "}
              </Text>
              {"for "}
              <Text as="span" fontWeight="bold">
                {`${schoolCount} ${schoolWord} `}
              </Text>
              {`updated by ${userRole} ${userFullName}. `}
              <Link
                color="primary.500"
                textDecoration="underline"
                href="#update"
                onClick={(e) => {
                  amplitude.track("Clicked see update");
                  e.preventDefault();
                  if (timestamp) {
                    onSelectTransaction({
                      transactionId: row.original.transactionId,
                      timestamp: formattedTimestamp,
                      userRole,
                      userFullName,
                    });
                  }
                }}
              >
                See update
              </Link>
              <Text as="span" color={"blackAlpha.700"}>
                {formattedTimestamp ? `. ${formattedTimestamp}` : ""}
              </Text>
            </Text>
          );
        },
      },
    ],
    [organization, glossary, onSelectTransaction]
  );

  return (
    <RemoteDataView
      remoteData={historyRD}
      error={(error) => <GenericError error={error as Error} />}
      loading={<HistoryLoading />}
    >
      {(data) => (
        <Table<HistoryRow>
          data={data.data}
          columns={columns}
          count={data.data.length}
          hideHeader={true}
        />
      )}
    </RemoteDataView>
  );
};

const HistoryLoading: React.FC = () => (
  <Flex direction="column" gap={2}>
    <Skeleton height="48px" />
    <Skeleton height="48px" />
    <Skeleton height="48px" />
  </Flex>
);
